import { Button, LinkButton } from '@upright/ui/common/generic/components/button';
import { StateMessage } from '@upright/ui/common/generic/components/state-message';
import { useMyOrganizationsQuery } from '@upright/ui/api/iam/organizations/hooks';
import { useLocation } from 'react-router-dom';
import { type ReactNode, useState } from 'react';
import { useGetLoggedInUser } from '@upright/ui/common/generic/hooks';
import styles from './page-state.module.css';

/** The white pane under the app header that a page-level `StateMessage` centres in. */
export function PageStatePane({ children, busy = false }: { children: ReactNode; busy?: boolean }) {
  return (
    <div className={styles.pane} aria-busy={busy}>
      {children}
    </div>
  );
}

export function PageState({
  title,
  description,
  onRetry,
  homeHref = '/',
  loginHref,
}: {
  title: string;
  description: string;
  onRetry?: () => void | Promise<unknown>;
  homeHref?: string;
  /** Offered as the primary action when there is no retry, e.g. to a signed-out visitor denied access. */
  loginHref?: string;
}) {
  const [retrying, setRetrying] = useState(false);
  const login = !onRetry && loginHref;
  const goHome = (
    <LinkButton href={homeHref} variant="outlined">
      Go to home
    </LinkButton>
  );
  return (
    <PageStatePane busy={retrying}>
      <StateMessage
        title={title}
        description={description}
        announce
        secondaryAction={onRetry || login ? goHome : undefined}
        primaryAction={
          login ? (
            <LinkButton href={login} variant="outlined">
              Log in
            </LinkButton>
          ) : onRetry ? (
            <Button
              variant="outlined"
              isDisabled={retrying}
              onPress={() => {
                setRetrying(true);
                void Promise.resolve()
                  .then(onRetry)
                  .catch(() => undefined)
                  .finally(() => {
                    setRetrying(false);
                  });
              }}
            >
              {retrying ? 'Trying again…' : 'Try again'}
            </Button>
          ) : (
            goHome
          )
        }
      />
    </PageStatePane>
  );
}

/** Keep query-backed navigation outside the presentational page and its Storybook states. */
export function usePageHomeHref() {
  const { search } = useLocation();
  const user = useGetLoggedInUser();
  const organizations = useMyOrganizationsQuery({ enabled: !!user });
  const requested = new URLSearchParams(search).get('organizationId');
  const organization = organizations.data?.organizations.find(org => String(org.id) === requested);
  return organization ? `/?organizationId=${organization.id}` : '/';
}
